import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { call, put, takeLatest, all } from 'redux-saga/effects';

import { RECORD_EVENT_REQUEST } from 'redux/analytics/constants';
import { addAPIError, removeAPIError } from 'redux/apiErrors/sagas';

import {
  getReservationCart,
  getReservationCartVirtualCart,
  updateReservationCart,
  createReservationCart,
  processReservationCart,
  processPayStackReservationCart,
  addCouponCode,
  createReservationCartCustomerAndSetupIntent
} from './api';
import {
  UPDATE_RESERVATION_CART_REQUEST,
  GET_RESERVATION_CART_REQUEST,
  GET_RESERVATION_CART_OBJECT_REQUESTING,
  GET_RESERVATION_CART_SUCCESS,
  RESET_RESERVATION_CART_ISFETCHING,
  PROCESS_RESERVATION_CART_REQUEST,
  PROCESS_PAY_STACK_RESERVATION_CART_UP_REQUEST,
  CREATE_RESERVATION_CART_REQUEST,
  ADD_RESERVATION_CART_COUPON_REQUEST,
  CREATE_RESERVATION_CART_CUSTOMER_AND_SETUP_INTENT_REQUEST
} from './constants';

export function* get() {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_RESERVATION_CART_OBJECT_REQUESTING });
    if (!reservationCartId()) {
      yield put({ type: GET_RESERVATION_CART_SUCCESS, payload: {} });
    } else {
      const response = yield call(getReservationCart, reservationCartId());
      const reservationCart = response.data;

      const { virtualCart } = yield call(getVirtualCart);
      reservationCart.virtualCart = virtualCart;

      yield put({
        type: GET_RESERVATION_CART_SUCCESS,
        payload: reservationCart
      });
    }
  } catch (error) {
    if (error.response && error.response.status === 404) {
      Cookies.remove('reservationCartId');
      yield put({ type: GET_RESERVATION_CART_SUCCESS, payload: {} });
    } else {
      yield put({ type: RESET_RESERVATION_CART_ISFETCHING });
      yield call(addAPIError, error);
    }
  }
}

function* create(action) {
  try {
    const newReservationCart = action.reservationCart;
    newReservationCart.channel = 'Web';
    if (!reservationCartId()) {
      if (Cookies.get('customerToken')) {
        newReservationCart.customerId = jwtDecode(
          Cookies.get('customerToken')
        ).sub;
      }
      newReservationCart.connectionInformation = {
        userAgent: navigator.userAgent
      };
      yield put({ type: GET_RESERVATION_CART_OBJECT_REQUESTING });
      const response = yield call(createReservationCart, newReservationCart);
      const reservationCart = response.data;
      Cookies.set('reservationCartId', response.data.id, {
        expires: 365,
        secure: process.env.APP_MODE === 'production'
      });

      const { virtualCart } = yield call(getVirtualCart);
      reservationCart.virtualCart = virtualCart;

      yield put({
        type: GET_RESERVATION_CART_SUCCESS,
        payload: reservationCart
      });
      yield put({
        type: RECORD_EVENT_REQUEST,
        eventName: 'selectReservation',
        payload: reservationCart
      });
      if (action.onSuccess) {
        action.onSuccess(reservationCart);
      }
    } else {
      const response = yield call(update, {
        reservationCart: newReservationCart,
        onSuccess: action.onSuccess,
        onError: action.onError
      });
      yield put({
        type: RECORD_EVENT_REQUEST,
        eventName: 'selectReservation',
        payload: response
      });
    }
  } catch (error) {
    yield put({ type: RESET_RESERVATION_CART_ISFETCHING });
    yield call(addAPIError, error);
  }
}

function* couponAdd(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_RESERVATION_CART_OBJECT_REQUESTING });

    const response = yield call(addCouponCode, reservationCartId(), {
      code: action.couponCode
    });

    const reservationCart = response.data;
    const { virtualCart } = yield call(getVirtualCart);
    reservationCart.virtualCart = virtualCart;

    yield put({ type: GET_RESERVATION_CART_SUCCESS, payload: reservationCart });
    const coupon = reservationCart.virtualCart?.coupons.find(
      (c) => c.code.toLowerCase() === action.couponCode.toLowerCase()
    );

    if (coupon?.inUse) {
      yield put({
        type: RECORD_EVENT_REQUEST,
        eventName: 'addCoupon',
        payload: coupon
      });
    }
    if (coupon?.inUse === false) {
      action.onError({ data: { message: coupon.invalidReason } });
    }
    if (action.onSuccess) {
      action.onSuccess(coupon?.inUse);
    }
  } catch (error) {
    yield put({ type: RESET_RESERVATION_CART_ISFETCHING });
    if (
      error.response &&
      (error.response.status === 404 || error.response.status === 422)
    ) {
      action.onError(error.response);
    }
  }
}

function* update(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_RESERVATION_CART_OBJECT_REQUESTING });
    const response = yield call(
      updateReservationCart,
      reservationCartId(),
      action.reservationCart
    );
    const reservationCart = response.data;

    const { virtualCart } = yield call(getVirtualCart);
    reservationCart.virtualCart = virtualCart;

    yield put({ type: GET_RESERVATION_CART_SUCCESS, payload: reservationCart });
    if (action.onSuccess) {
      action.onSuccess(reservationCart);
    }
    return reservationCart;
  } catch (error) {
    yield put({ type: RESET_RESERVATION_CART_ISFETCHING });
    yield call(addAPIError, error);
    if (action.onError) {
      action.onError();
    }
  }
  return null;
}

function* processForReservation(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_RESERVATION_CART_OBJECT_REQUESTING });
    const response = yield call(processReservationCart, reservationCartId());

    yield put({
      type: GET_RESERVATION_CART_SUCCESS,
      payload: response.data
    });
    Cookies.remove('reservationCartId');
    yield put({
      type: RECORD_EVENT_REQUEST,
      eventName: 'reservationCheckoutComplete',
      payload: response.data
    });
    if (action.onSuccess) {
      action.onSuccess(response.data);
    }
  } catch (error) {
    yield put({ type: RESET_RESERVATION_CART_ISFETCHING });

    if (error.response && error.response.status === 404) {
      action.onError();
    } else {
      yield call(addAPIError, error);
    }
  }
}

function* processPaystack(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_RESERVATION_CART_OBJECT_REQUESTING });
    const response = yield call(
      processPayStackReservationCart,
      reservationCartId(),
      action.tenderId
    );
    yield put({
      type: GET_RESERVATION_CART_SUCCESS,
      payload: response.data
    });
    Cookies.remove('reservationCartId');
    yield put({
      type: RECORD_EVENT_REQUEST,
      eventName: 'reservationCheckoutComplete',
      payload: response.data
    });
    if (action.onSuccess) {
      action.onSuccess(response.data);
    }
  } catch (error) {
    yield put({ type: RESET_RESERVATION_CART_ISFETCHING });
    yield call(addAPIError, error);
    if (action.onError) {
      action.onError();
    }
  }
}

function* createCustomerAndSetupIntentSaga(action) {
  try {
    yield call(removeAPIError);
    const response = yield call(
      createReservationCartCustomerAndSetupIntent,
      reservationCartId(),
      action.customerPayload
    );
    if (action.onSuccess) {
      action.onSuccess(response.data);
    }
  } catch (error) {
    yield call(addAPIError, error);
    if (action.onError) {
      action.onError();
    }
  }
}

function* getVirtualCart() {
  let virtualCart;
  try {
    const response = yield call(
      getReservationCartVirtualCart,
      reservationCartId()
    );
    virtualCart = response.data;
  } catch (err) {
    if (![404, 422, 429].includes(err.response.status)) {
      yield call(addAPIError, err);
    }
    yield put({ type: RESET_RESERVATION_CART_ISFETCHING });
  }
  return { virtualCart };
}

function* watchList() {
  yield takeLatest(GET_RESERVATION_CART_REQUEST, get);
  yield takeLatest(UPDATE_RESERVATION_CART_REQUEST, update);
  yield takeLatest(PROCESS_RESERVATION_CART_REQUEST, processForReservation);
  yield takeLatest(ADD_RESERVATION_CART_COUPON_REQUEST, couponAdd);
  yield takeLatest(
    PROCESS_PAY_STACK_RESERVATION_CART_UP_REQUEST,
    processPaystack
  );
  yield takeLatest(CREATE_RESERVATION_CART_REQUEST, create);
  yield takeLatest(
    CREATE_RESERVATION_CART_CUSTOMER_AND_SETUP_INTENT_REQUEST,
    createCustomerAndSetupIntentSaga
  );
}

export default function* reservationCartSagas() {
  yield all([watchList()]);
}

const reservationCartId = () => Cookies.get('reservationCartId');
